import slugify from "slugify"
import React from "react"
import { SinglePathSVG } from "../SVG"
import { HR, Spacer } from "../atoms/Atoms"
import L10n from "../../../lib/carrot-l10n"
import Helpers from "../../../utils/Helpers"
import LibraryMarkdown from "./LibraryMarkdown"
import { HashLink } from "react-router-hash-link"
import { BodySmall } from "../atoms/Bodies"
import { Header } from "components/views/atoms/Header"
import { H1, H5 } from "@carrotfertility/carotene"
import { BodyWithRightSidebarLayout } from "../atoms/layouts/BodyWithRightSidebarLayout"
import styled from "styled-components"
import { Large, NotLarge } from "../atoms/Responsive"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const BreadCrumbLink = ({ to, children, weight }) => (
  <H5>
    <HashLink to={to} className={`${weight} no-underline black-80 ml1`}>
      {children}
    </HashLink>
  </H5>
)

const BreadCrumbArrow = () => (
  <div className="mh2 black-60">
    <SinglePathSVG icon="rightCarrotSmall" />
  </div>
)

const LibraryBreadcrumbs = ({ categoryTitle, hasManySubCategories, subCategory }) => {
  const subCategorySlug = slugify(subCategory.fields.navTitle || subCategory.fields.title)
  const libraryUrl = "/read"
  const categoryUrl = `${libraryUrl}/${slugify(categoryTitle)}`
  const subCategoryUrl = `${categoryUrl}#${subCategorySlug}`

  return (
    <div className="flex items-center" style={{ marginBottom: "6px" }}>
      <div className="mr1">
        <BodySmall color="black-60">{l`client.library.readAndLearn`}</BodySmall>
      </div>
      <BreadCrumbArrow />
      <BreadCrumbLink weight={hasManySubCategories ? "normal" : "b"} to={categoryUrl}>
        {categoryTitle}
      </BreadCrumbLink>
      {hasManySubCategories && (
        <>
          <BreadCrumbArrow />
          <BreadCrumbLink to={subCategoryUrl}>{subCategory.fields.navTitle || subCategory.fields.title}</BreadCrumbLink>
        </>
      )}
    </div>
  )
}

const ReadArticleHeader = ({ title, hasManySubCategories, subCategory }) => (
  <div className="w-100">
    <Header
      titleBar={
        <LibraryBreadcrumbs
          categoryTitle={title}
          hasManySubCategories={hasManySubCategories}
          subCategory={subCategory}
        />
      }
    />
  </div>
)

const LastUpdatedTag = ({ lastUpdated, isVideo }) => (
  <>
    <Spacer height={isVideo ? 2.5 : 3} />
    <div style={{ width: 47 }}>
      <HR width={3} color="black-10" />
    </div>
    <Spacer height={0.75} />
    <BodySmall color="black-80">Last updated: {Helpers.formatDate(lastUpdated, "MM/DD/YYYY")}</BodySmall>
  </>
)

const StyledArticleLegalFooter = styled.div`
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 150%;
  width: 66.66667%;
  max-width: 600px;
  min-width: 384px;
  @media screen and (min-width: 596px) {
    width: 100%;
  }
`

const ArticleLegalFooter = ({ source }) => <StyledArticleLegalFooter>{source}</StyledArticleLegalFooter>

const StyledArticleLegalSidebar = styled.div`
  background-color: rgba(245, 245, 245, 1);
  border-radius: 8px;
  padding: 24px;
  color: rgba(0, 0, 0, 0.8);
  font-size: 14px;
  line-height: 150%;
`

const ArticleLegalSidebar = ({ legalNoteTop, legalNoteBottom }) => (
  <>
    <Spacer height={3} />
    <NotLarge>
      <HR color="mineral-grey" />
      <Spacer height={1.5} />
    </NotLarge>
    <StyledArticleLegalSidebar>
      <div>{legalNoteTop}</div>
      <Spacer height={1.5} />
      <div style={{ width: 47 }}>
        <HR width={3} color="black-10" />
      </div>
      <Spacer height={1.5} />
      <div>{legalNoteBottom}</div>
    </StyledArticleLegalSidebar>
  </>
)

const ReadArticle = ({
  articleBody,
  articleTitle,
  categoryTitle,
  hasManySubCategories,
  isVideo,
  lastUpdated,
  subCategory,
  videoUrl
}) => (
  <>
    <ReadArticleHeader hasManySubCategories={hasManySubCategories} subCategory={subCategory} title={categoryTitle} />
    <BodyWithRightSidebarLayout
      sidebar={
        <ArticleLegalSidebar
          legalNoteTop={l`Legality and availability of services vary from country to country and often even within a country. Contact your Care Team for help understanding what services are available to you domestically or through cross-border care.`}
          legalNoteBottom={l`We strive to be as clear and inclusive as possible. When appropriate, we use terminology that reflects current medical practice. We recognize that these terms do not capture every individual’s experience. `}
        />
      }
    >
      <div className="flex justify-center w-100">
        <div className={`${isVideo ? "mw-800 w-100" : "mw-606"}`}>
          <Spacer height={3} />
          <H1>
            <span style={{ fontSize: 40 }}>{articleTitle}</span>
          </H1>
          <Spacer height={3} />
          {isVideo ? (
            <div className="embed-container">
              <iframe className="bn" title={articleTitle} src={videoUrl} frameBorder="0" allowFullScreen />
            </div>
          ) : (
            <LibraryMarkdown source={articleBody} additionalClasses="publico-text black-80" />
          )}
          <LastUpdatedTag lastUpdated={lastUpdated} isVideo={isVideo} />
        </div>
      </div>
    </BodyWithRightSidebarLayout>
    <Large>
      <Spacer height={3} />
      <HR color="mineral-grey" />
    </Large>

    <Spacer height={1.5} />
    <ArticleLegalFooter
      source={l`Carrot resources are for informational purposes only. They are not a substitute for legal, tax, or professional medical advice, nor medical diagnosis or treatment. If you are experiencing an emergency, seek local emergency care immediately.`}
    />
  </>
)

export default ReadArticle
export { LibraryBreadcrumbs }
